import { useContext } from 'react'
import { LuCalendar } from 'react-icons/lu'

import { findTaskSchedule, TaskDefinition, TaskStatusDropdown } from '@/feature/execution-board'
import { ActiveAssetContext } from '@/feature/observatory/contexts/ActiveAssetContext'
import { usePersistentObservatoryPageStore } from '@/feature/observatory/store/usePersistentObservatoryPageStore'

import { Loader } from '@/lib/core/components/Loader'
import { formatTimestamp } from '@/lib/core/constants/constants'

import styles from './AssetPanel.module.css'

interface AssetTaskTabProps {
  tasks: TaskDefinition[]
  isLoading: boolean
}

interface TaskCardProps {
  task: TaskDefinition
}

const TaskCard = ({ task }: TaskCardProps) => {
  const taskDef = task.taskDefinition
  const startLocation = taskDef.locations?.[0]
  const endLocation = taskDef.locations?.[taskDef.locations.length - 1]
  const schedule = findTaskSchedule(task)

  const setFocusedTaskId = usePersistentObservatoryPageStore((state) => state.setFocusedTaskId)
  const setTasks = usePersistentObservatoryPageStore((state) => state.toggles.setTasks)
  const { clearActiveAsset } = useContext(ActiveAssetContext)

  const orgAssignedToTask = (task: TaskDefinition) => {
    if (task.onwardAssignedToOrg) {
      return task.onwardAssignedToOrg.name
    }
    return task.assignedByOrg.name
  }

  // We're already on the map view, so focus the task here instead of navigating to the Execution
  // Board. Enable the task layer so its route renders, and close the asset panel for a clean
  // transition into Task Focus Mode.
  const handleFocusTask = () => {
    setTasks(true)
    setFocusedTaskId(task.id)
    clearActiveAsset()
  }

  return (
    <div className={styles.taskCard}>
      <div className={styles.column}>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Reference:</span>
          {taskDef.reference ? (
            <button
              type="button"
              className={styles.referenceLinkButton}
              onClick={handleFocusTask}
              data-testid="view-task-button"
              title="Focus on map"
            >
              {taskDef.reference}
            </button>
          ) : (
            <span className={styles.detailValue}>-</span>
          )}
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Issued By:</span>
          <span className={styles.detailValue}>{task.assignedByOrg.name}</span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Assigned To:</span>
          <span className={styles.detailValue}>{orgAssignedToTask(task)}</span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Description:</span>
          <span className={styles.detailValue}>{taskDef.description || '-'}</span>
        </div>
      </div>

      <div className={styles.column}>
        <div className={styles.locationBlock}>
          <span className={styles.locationName}>
            {startLocation?.name || startLocation?.address || '-'}
          </span>
          <div className={styles.locationTimes}>
            <span className={styles.timeItem}>
              <LuCalendar size={14} />
              {schedule ? formatTimestamp(schedule.start) : '--:--'}
            </span>
          </div>
        </div>
        <div className={styles.locationBlock}>
          <span className={styles.locationName}>
            {endLocation?.name || endLocation?.address || '-'}
          </span>
          <div className={styles.locationTimes}>
            <span className={styles.timeItem}>
              <LuCalendar size={14} />
              {schedule ? formatTimestamp(schedule.end) : '--:--'}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.column}>
        <TaskStatusDropdown currentStatus={task.status} displayOnly />
      </div>
    </div>
  )
}

export const AssetTaskTab = ({ tasks, isLoading }: AssetTaskTabProps) => {
  if (isLoading) {
    return (
      <div className={styles.taskList}>
        <Loader size="2rem" />
      </div>
    )
  }

  if (tasks.length === 0) {
    return (
      <div className={styles.taskList}>
        <span className={styles.detailValue}>No active task assigned</span>
      </div>
    )
  }

  return (
    <div className={styles.taskList}>
      {tasks.map((task) => (
        <TaskCard key={task.id} task={task} />
      ))}
    </div>
  )
}
